import keyBy from 'lodash/keyBy';
import { useState, useEffect, useCallback, useMemo } from 'react';

import noop from 'utils/noop';
import { PrimaryButton, BUTTON_SIZES } from 'ui/button';
import { Modal, MODAL_SIZES, ModalHeader, ModalBody, ModalFooter } from 'ui/modal';
import { MultiSelect } from 'ui/selectv2/MultiSelect';
import { FormField } from 'ui/formField';
import { BaseOption } from 'ui/selectv2/types';
import { Body2 } from 'ui/theme';

import { useDashboardFilterOptions } from './useDashboardFilterOptions';
import { useMissionControlContext } from 'modules/missionControl/contexts/MissionControlContext';

export const DASHBOARD_FILTER__MODAL_TEST_ID = 'dashboard-filter-modal';
export const DASHBOARD_FILTER_BUTTON_TEST_ID = 'dashboard-filter-button';

const getOption = (map: Record<string, BaseOption>, id: number) =>
  map[id] || { id, value: id, label: id };

type DashboardFiltersModalProps = {
  onClose?: () => void;
};

export const DashboardFiltersModal = ({ onClose = noop }: DashboardFiltersModalProps) => {
  const {
    labelIds,
    messageTypeIds,
    setLabelIds,
    setMessageTypeIds,
    trackFilters,
    missionControlUserPreferencesQuery: { userPreferences },
  } = useMissionControlContext();
  const { labelsOptions, messageTypesOptions } = useDashboardFilterOptions();

  const labelsMap = useMemo(() => keyBy(labelsOptions, 'id'), [labelsOptions]);
  const messageTypesMap = useMemo(() => keyBy(messageTypesOptions, 'id'), [messageTypesOptions]);

  const [selectedLabels, setSelectedLabels] = useState<BaseOption[]>(() =>
    labelIds.map((id) => getOption(labelsMap, id)),
  );

  const [selectedMessageTypes, setSelectedMessageTypes] = useState<BaseOption[]>(() =>
    messageTypeIds.map((id) => getOption(messageTypesMap, id)),
  );

  useEffect(() => {
    if (userPreferences) {
      setSelectedLabels(userPreferences.campaignLabelIds.map((id) => getOption(labelsMap, id)));
      setSelectedMessageTypes(
        userPreferences.messageTypeIds.map((id) => getOption(messageTypesMap, id)),
      );
    }
  }, [userPreferences, labelsMap, messageTypesMap]);

  // Sync state with props when they change
  useEffect(() => {
    setSelectedLabels(labelIds.map((id) => getOption(labelsMap, id)));
  }, [labelIds, labelsMap]);

  useEffect(() => {
    setSelectedMessageTypes(messageTypeIds.map((id) => getOption(messageTypesMap, id)));
  }, [messageTypeIds, messageTypesMap]);

  const handleApplyFilters = useCallback(() => {
    const selectedLabelIds = selectedLabels.map((v) => Number(v.value));
    const selectedMessageTypeIds = selectedMessageTypes.map((v) => Number(v.value));

    setLabelIds(selectedLabelIds);
    setMessageTypeIds(selectedMessageTypeIds);
    trackFilters({
      campaignLabelIds: selectedLabelIds,
      messageTypeIds: selectedMessageTypeIds,
    });
    onClose();
  }, [selectedLabels, selectedMessageTypes, setLabelIds, setMessageTypeIds, onClose, trackFilters]);

  return (
    <Modal
      withPortal
      isModalOpen={true}
      onClose={onClose}
      id={DASHBOARD_FILTER__MODAL_TEST_ID}
      data-test={DASHBOARD_FILTER__MODAL_TEST_ID}
      size={MODAL_SIZES.MEDIUM}
    >
      <ModalHeader>Apply filters</ModalHeader>
      <ModalBody>
        <Body2>
          Create a custom filtered view that only you can see to personalize your home page.
        </Body2>

        <FormField labelText="Label">
          <MultiSelect
            usePortal
            value={selectedLabels}
            options={labelsOptions}
            placeholder="Search or select a label"
            onSelect={setSelectedLabels}
          />
        </FormField>

        <FormField labelText="Message type">
          <MultiSelect
            usePortal
            value={selectedMessageTypes}
            options={messageTypesOptions}
            placeholder="Search or select a message type"
            onSelect={setSelectedMessageTypes}
          />
        </FormField>
      </ModalBody>
      <ModalFooter>
        <PrimaryButton
          onClick={handleApplyFilters}
          size={BUTTON_SIZES.LARGE}
          data-test={DASHBOARD_FILTER_BUTTON_TEST_ID}
        >
          Apply filters
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
